import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Layers, Search, Send, X } from 'lucide-react';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { createBidFromCatalogItem, createCustomBid } from '../../services/alaCarteBidService';
import { getCrmCompanyLogoDisplayUrl } from '../../services/firestoreService';
import { normalizeAlaCarteItem, PRICING_STATUS as CATALOG_STATUS } from '../../utils/alaCarteCatalog';
import { FIELD_CLASS, LABEL_CLASS, FilterSelect } from './CrmFilterUi';
import BidPackageModal from './BidPackageModal';
import BidPricingFields, { INITIAL_BID_PRICING, buildDirectPricing } from './BidPricingFields';
import { TEAM_ALACARTE_DEPARTMENTS } from '../../utils/teamAlaCarteRoutes';
import { isActiveClientAccount } from '../../utils/accountLifecycle';
import { INTERNAL_BID_ACCOUNT_ID, INTERNAL_BID_COMPANY, bidAccountTypeLabel } from '../../utils/alaCarteBid';

function accountContactName(account) {
  const c = account?.contact || {};
  const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
  return full || c.name || '';
}

const CLOSED_LEAD_STAGES = new Set(['lost', 'archived']);

/** Service dropdown value for a one-off service typed in by hand. */
const CUSTOM_SERVICE = '__custom__';

/** Open lead: not Lost/Archived (stage or result) and not deleted. */
function isOpenLead(account) {
  if (account?.type !== 'Leads') return false;
  if (account.isDeleted === true || account.deleted === true) return false;
  const stage = String(account.stage || '').toLowerCase();
  const result = String(account.result || '').toLowerCase();
  return !CLOSED_LEAD_STAGES.has(stage) && !CLOSED_LEAD_STAGES.has(result);
}

const ACCOUNT_TABS = [
  { value: 'Clients', label: 'Clients', noun: 'clients' },
  { value: 'Leads', label: 'Leads', noun: 'leads' },
  { value: 'Internal', label: 'Internal', noun: 'internal teams' },
];

/** Stand-in account row for BBA's own work — picked like any account, no accounts doc behind it. */
const INTERNAL_ACCOUNT = {
  id: INTERNAL_BID_ACCOUNT_ID,
  type: 'Internal',
  company: INTERNAL_BID_COMPANY.name,
  crmCompanyLogo: INTERNAL_BID_COMPANY.logoUrl,
  contact: { name: 'Internal team' },
};

/** Square company logo with an initial fallback — same chip as the Tasks assignee picker. */
function AccountLogo({ account }) {
  const logoUrl = getCrmCompanyLogoDisplayUrl(account);
  return (
    <div className="relative mr-3 flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 text-sm font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="absolute inset-0 z-10 h-full w-full bg-white object-contain p-0.5 dark:bg-gray-700"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      ) : null}
      <span>{(account.company?.[0] || '?').toUpperCase()}</span>
    </div>
  );
}

/**
 * Bids board "+" — pick a client or lead, then send one case-by-case service out for bid
 * (or hand off to the package builder to quote several together).
 *
 * @param {object} props
 * @param {object[]} props.accounts Every CRM account (clients + leads).
 * @param {object[]} props.bids Existing bids, to flag services already quoted for the account.
 * @param {() => void} props.onClose
 * @param {() => void} props.onCreated
 */
export default function NewBidModal({ accounts, bids, onClose, onCreated }) {
  const { currentUser } = useAuth();
  const [catalogItems, setCatalogItems] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountTab, setAccountTab] = useState('Clients');
  const [accountId, setAccountId] = useState('');
  const [catalogItemId, setCatalogItemId] = useState('');
  const [postToTeam, setPostToTeam] = useState(true);
  const [team, setTeam] = useState('');
  const [customName, setCustomName] = useState('');
  const [customIcon, setCustomIcon] = useState('✏️');
  const [customDescription, setCustomDescription] = useState('');
  const [pricing, setPricing] = useState(INITIAL_BID_PRICING);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [packageMode, setPackageMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, 'alaCarteItems'))
      .then((snap) => {
        if (!cancelled) setCatalogItems(snap.docs.map((d) => normalizeAlaCarteItem(d.id, d.data())));
      })
      .catch((e) => console.error(e));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (packageMode) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, packageMode]);

  const selectableAccounts = useMemo(
    () => [
      ...accounts
        .filter((a) => (a.type === 'Clients' && isActiveClientAccount(a)) || isOpenLead(a))
        .sort((a, b) => (a.company || '').localeCompare(b.company || '')),
      INTERNAL_ACCOUNT,
    ],
    [accounts]
  );

  const tabCounts = useMemo(() => {
    const counts = { Clients: 0, Leads: 0, Internal: 0 };
    selectableAccounts.forEach((a) => {
      counts[a.type] += 1;
    });
    return counts;
  }, [selectableAccounts]);

  const matchingAccounts = useMemo(() => {
    const q = accountSearch.trim().toLowerCase();
    return selectableAccounts
      .filter((a) => a.type === accountTab)
      .filter(
        (a) =>
          !q ||
          (a.company || '').toLowerCase().includes(q) ||
          accountContactName(a).toLowerCase().includes(q) ||
          (a.contact?.email || '').toLowerCase().includes(q)
      );
  }, [selectableAccounts, accountTab, accountSearch]);

  const activeTab = ACCOUNT_TABS.find((tab) => tab.value === accountTab) || ACCOUNT_TABS[0];

  const selectedAccount = useMemo(
    () => selectableAccounts.find((a) => a.id === accountId) || null,
    [selectableAccounts, accountId]
  );

  const caseByCaseItems = useMemo(
    () =>
      catalogItems
        .filter((item) => item.pricingType === 'caseByCase')
        .filter((item) => item.pricingStatus !== CATALOG_STATUS.unpromoted)
        .sort(
          (a, b) =>
            (a.department || '').localeCompare(b.department || '') || (a.name || '').localeCompare(b.name || '')
        ),
    [catalogItems]
  );

  /** Catalog item ids this account already has a bid (or package line) for. */
  const quotedItemIds = useMemo(() => {
    const ids = new Set();
    if (!accountId) return ids;
    bids
      .filter((b) => b.accountId === accountId)
      .forEach((b) => {
        if (b.catalogItemId) ids.add(b.catalogItemId);
        (b.packageItems || []).forEach((item) => item.catalogItemId && ids.add(item.catalogItemId));
      });
    return ids;
  }, [bids, accountId]);

  const itemsByDepartment = useMemo(() => {
    const map = new Map();
    caseByCaseItems.forEach((item) => {
      const dept = item.department || 'General';
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept).push(item);
    });
    return [...map.entries()];
  }, [caseByCaseItems]);

  const selectedItem = useMemo(
    () => caseByCaseItems.find((item) => item.id === catalogItemId) || null,
    [caseByCaseItems, catalogItemId]
  );

  const isCustom = catalogItemId === CUSTOM_SERVICE;

  /** Team the bid belongs to — the service's own department until someone picks another. */
  const effectiveTeam = team || selectedItem?.department || '';

  const teamOptions = useMemo(
    () => [...new Set([...TEAM_ALACARTE_DEPARTMENTS, selectedItem?.department].filter(Boolean))],
    [selectedItem]
  );

  const handleCreate = async () => {
    if (!accountId || !catalogItemId || saving) return;
    if (isCustom && !customName.trim()) {
      setError('Name the service.');
      return;
    }
    if (isCustom && !effectiveTeam) {
      setError('Choose the team this service belongs to.');
      return;
    }
    const { directPricing, error: pricingError } = buildDirectPricing(pricing);
    if (pricingError) {
      setError(pricingError);
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isCustom) {
        await createCustomBid({
          accountId,
          name: customName,
          icon: customIcon,
          description: customDescription,
          department: effectiveTeam,
          postedByUid: currentUser?.uid || '',
          postToTeam,
          directPricing,
        });
      } else {
        await createBidFromCatalogItem({
          accountId,
          catalogItemId,
          source: 'crm',
          postedByUid: currentUser?.uid || '',
          postToTeam,
          department: effectiveTeam,
          directPricing,
        });
      }
      onCreated();
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Could not create the bid.');
    } finally {
      setSaving(false);
    }
  };

  if (packageMode && selectedAccount) {
    return (
      <BidPackageModal
        accountId={selectedAccount.id}
        companyName={selectedAccount.company || ''}
        catalogItems={caseByCaseItems.filter((item) => !quotedItemIds.has(item.id))}
        preselectedItemIds={catalogItemId && !isCustom && !quotedItemIds.has(catalogItemId) ? [catalogItemId] : []}
        onClose={() => setPackageMode(false)}
        onCreated={() => onCreated()}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[88vh] w-full max-w-xl flex-col rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="New bid"
      >
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          <div className="w-9" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">New bid</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div>
            <span className={LABEL_CLASS}>Client, lead, or internal</span>
            {selectedAccount ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/60">
                <div className="flex min-w-0 items-center">
                <AccountLogo account={selectedAccount} />
                <div className="min-w-0">
                  <p className="truncate font-medium text-gray-900 dark:text-white">
                    {selectedAccount.company || 'Unnamed company'}
                    <span className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 text-[10px] uppercase text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {bidAccountTypeLabel(selectedAccount.type)}
                    </span>
                  </p>
                  {accountContactName(selectedAccount) && (
                    <p className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {accountContactName(selectedAccount)}
                    </p>
                  )}
                </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setAccountId('');
                    setCatalogItemId('');
                  }}
                  className="shrink-0 text-sm font-medium text-red-600 hover:underline dark:text-red-400"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="mb-3 flex border-b border-gray-200 dark:border-gray-700">
                  {ACCOUNT_TABS.map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setAccountTab(tab.value)}
                      className={`flex-1 px-4 py-2 text-sm font-medium ${
                        accountTab === tab.value
                          ? 'border-b-2 border-red-500 text-red-500'
                          : 'text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
                      }`}
                    >
                      {tab.label}
                      <span className="ml-1.5 text-xs font-normal text-gray-400">{tabCounts[tab.value]}</span>
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    value={accountSearch}
                    onChange={(e) => setAccountSearch(e.target.value)}
                    placeholder={`Search ${activeTab.noun} by company, contact, or email…`}
                    className={`${FIELD_CLASS} pl-10 pr-10`}
                  />
                  {accountSearch && (
                    <button
                      type="button"
                      onClick={() => setAccountSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                      title="Clear search"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <div className="mt-3 max-h-[22rem] space-y-2 overflow-y-auto">
                  {matchingAccounts.length === 0 ? (
                    <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      {accountSearch.trim()
                        ? `No ${activeTab.noun} match "${accountSearch.trim()}".`
                        : `No ${activeTab.noun} yet.`}
                    </p>
                  ) : (
                    matchingAccounts.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setAccountId(a.id)}
                        className="flex w-full items-center rounded-xl border border-gray-200 p-3 text-left hover:border-red-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-red-800 dark:hover:bg-gray-800"
                      >
                        <AccountLogo account={a} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-gray-900 dark:text-white">
                            {a.company || 'Unnamed company'}
                          </span>
                          {accountContactName(a) && (
                            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">
                              {accountContactName(a)}
                            </span>
                          )}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {selectedAccount && (
            <>
              <div>
                <label htmlFor="new-bid-service" className={LABEL_CLASS}>
                  Service
                </label>
                <FilterSelect
                  id="new-bid-service"
                  icon={false}
                  value={catalogItemId}
                  onChange={(e) => setCatalogItemId(e.target.value)}
                >
                  <option value="">Choose a case-by-case service…</option>
                  <option value={CUSTOM_SERVICE}>➕ Add my own service…</option>
                  {itemsByDepartment.map(([dept, items]) => (
                    <optgroup key={dept} label={dept}>
                      {items.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.icon || '📦'} {item.name}
                          {quotedItemIds.has(item.id) ? ' — already bid' : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </FilterSelect>
                {catalogItemId && quotedItemIds.has(catalogItemId) && (
                  <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">
                    {selectedAccount.company || 'This account'} already has a bid for this service.
                  </p>
                )}
              </div>

              {isCustom && (
                <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
                  <div className="grid grid-cols-[4.5rem,1fr] gap-3">
                    <div>
                      <label htmlFor="new-bid-custom-icon" className={LABEL_CLASS}>
                        Icon
                      </label>
                      <input
                        id="new-bid-custom-icon"
                        type="text"
                        maxLength={4}
                        value={customIcon}
                        onChange={(e) => setCustomIcon(e.target.value)}
                        className={`${FIELD_CLASS} px-2 text-center text-lg`}
                      />
                    </div>
                    <div>
                      <label htmlFor="new-bid-custom-name" className={LABEL_CLASS}>
                        Service name
                      </label>
                      <input
                        id="new-bid-custom-name"
                        type="text"
                        autoFocus
                        value={customName}
                        onChange={(e) => setCustomName(e.target.value)}
                        placeholder="e.g. Trade show booth design"
                        className={`${FIELD_CLASS} px-3`}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="new-bid-custom-description" className={LABEL_CLASS}>
                      What the client asked for <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <textarea
                      id="new-bid-custom-description"
                      rows={2}
                      value={customDescription}
                      onChange={(e) => setCustomDescription(e.target.value)}
                      className={`${FIELD_CLASS} px-3`}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    One-off for this bid only — it isn&apos;t added to the à la carte catalog.
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="new-bid-team" className={LABEL_CLASS}>
                  Team
                </label>
                <FilterSelect
                  id="new-bid-team"
                  icon={false}
                  value={effectiveTeam}
                  onChange={(e) => setTeam(e.target.value)}
                >
                  {!effectiveTeam && <option value="">Choose a team…</option>}
                  {teamOptions.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </FilterSelect>
              </div>

              <BidPricingFields
                pricing={pricing}
                onChange={setPricing}
                team={effectiveTeam}
                teamExtras={
                  <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={postToTeam}
                      onChange={(e) => setPostToTeam(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    Send it now (otherwise saved as a draft)
                  </label>
                }
              />
            </>
          )}

          {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-800">
          <button
            type="button"
            disabled={!selectedAccount}
            onClick={() => setPackageMode(true)}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            <Layers className="h-4 w-4" />
            Build a package instead
          </button>
          <button
            type="button"
            disabled={!accountId || !catalogItemId || saving}
            onClick={handleCreate}
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {saving
              ? 'Creating…'
              : pricing.mode === 'direct'
                ? pricing.publish
                  ? 'Create & publish'
                  : 'Save priced bid'
                : postToTeam
                  ? 'Send out for bid'
                  : 'Save draft'}
          </button>
        </div>
      </div>
    </div>
  );
}
